import React, { useCallback, useState } from 'react';

import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Switch } from 'shadcn/components/ui/Switch';
import { Textarea } from 'shadcn/components/ui/Textarea';
import { FacilityPickerV2 } from 'shared/components/FacilityPickerV2';

import { BuyersPicker } from '../formComponents/BuyersPicker';
import { CrossDockPicker } from '../formComponents/CrossDockPicker';
import { ShippersPicker } from '../formComponents/ShippersPicker';
import { VendorPicker } from '../formComponents/VendorPicker';
import { DELIVERY_TYPE_DELIVERED, DELIVERY_TYPE_PICKUP } from '../utils/purchaseOrderDisplayUtils';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';

/**
 * @param {Object} props
 * @param {Object} props.draft
 * @param {(updater: (prev: any) => any) => void} props.onDraftChange
 * @param {(value: string) => void} [props.onScheduledDeliveryDateInputChange]
 * @param {(date: string) => void} [props.onScheduledDeliveryDateChange]
 * @param {string} [props.scheduledDeliveryDateInput]
 * @param {boolean} [props.showValidation]
 * @param {string[]} [props.primaryErrors]
 * @param {Object} [props.primaryFieldErrors]
 */
export function PurchaseOrderCreatePrimaryCard({
    draft,
    onDraftChange,
    onScheduledDeliveryDateChange,
    onScheduledDeliveryDateInputChange,
    primaryErrors = [],
    primaryFieldErrors = {},
    scheduledDeliveryDateInput = '',
    showValidation = false,
}) {
    const [showCrossDockFields, setShowCrossDockFields] = useState(false);
    const [crossDockConfirmOpen, setCrossDockConfirmOpen] = useState(false);

    const setDraftField = useCallback(
        (field, value) => {
            onDraftChange((prev) => ({
                ...prev,
                [field]: value,
            }));
        },
        [onDraftChange],
    );

    const crossDockEnabled = showCrossDockFields || draft.isReceivedAtCrossDock === true;
    const crossDockDataPresent =
        draft.isReceivedAtCrossDock === true || Boolean(draft.crossDockId) || Boolean(draft.crossDockDeliveryDate);

    const clearCrossDockDraftFields = useCallback(() => {
        setDraftField('isReceivedAtCrossDock', false);
        setDraftField('crossDockId', '');
        setDraftField('crossDockDeliveryDate', '');
    }, [setDraftField]);

    const handleInputChange = useCallback(
        (field) => (event) => {
            setDraftField(field, event?.target?.value ?? '');
        },
        [setDraftField],
    );

    const handleScheduledDeliveryDateChange = useCallback(
        (event) => {
            const raw = String(event?.target?.value ?? '');
            if (typeof onScheduledDeliveryDateInputChange === 'function') {
                onScheduledDeliveryDateInputChange(raw);
            }
        },
        [onScheduledDeliveryDateInputChange],
    );

    const handleScheduledDeliveryDateBlur = useCallback(() => {
        const raw = String(scheduledDeliveryDateInput ?? '');
        if (raw === String(draft?.scheduledDeliveryDate ?? '')) return;
        if (typeof onScheduledDeliveryDateChange === 'function') {
            onScheduledDeliveryDateChange(raw);
            return;
        }
        onDraftChange((prev) => ({
            ...prev,
            scheduledDeliveryDate: raw,
            // When a date is selected and time is empty, default to midnight.
            scheduledTime: raw ? prev?.scheduledTime || '00:00' : '',
        }));
    }, [draft?.scheduledDeliveryDate, onDraftChange, onScheduledDeliveryDateChange, scheduledDeliveryDateInput]);

    const handleScheduledTimeChange = useCallback(
        (event) => {
            const raw = String(event?.target?.value ?? '');
            setDraftField('scheduledTime', raw);
        },
        [setDraftField],
    );

    const handleCrossDockSwitchChange = useCallback(
        (checked) => {
            // Turning ON is always safe.
            if (checked === true) {
                setShowCrossDockFields(true);
                return;
            }

            // Turning OFF should confirm if there is already cross dock data filled in.
            if (crossDockDataPresent) {
                setCrossDockConfirmOpen(true);
                return;
            }

            // No data to lose: just hide and clear.
            setShowCrossDockFields(false);
            clearCrossDockDraftFields();
        },
        [clearCrossDockDraftFields, crossDockDataPresent],
    );

    const showErrors = showValidation === true;
    const getFieldError = useCallback(
        (field) => {
            if (!showErrors) return '';
            return primaryFieldErrors?.[field] ?? '';
        },
        [primaryFieldErrors, showErrors],
    );
    const renderFieldError = useCallback(
        (field) => {
            const message = getFieldError(field);
            if (!message) return null;
            return <p className="text-xs text-destructive">{message}</p>;
        },
        [getFieldError],
    );

    return (
        <Card>
            <CardHeader className="pb-0">
                <CardTitle>Purchase Order Information</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                {showErrors && primaryErrors.length > 0 ? (
                    <div className="pb-6">
                        <Alert variant="destructive">
                            <AlertTitle>Fix the following to save this purchase order</AlertTitle>
                            <AlertDescription>
                                <ul className="list-disc pl-5">
                                    {primaryErrors.map((message) => (
                                        <li key={message}>{message}</li>
                                    ))}
                                </ul>
                            </AlertDescription>
                        </Alert>
                    </div>
                ) : null}
                <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
                    <div className="flex flex-col gap-2">
                        <fieldset className="flex flex-col gap-3">
                            <legend className={labelClassName} style={{ paddingBottom: '15px' }}>
                                Delivery Type <span className="text-destructive">*</span>
                            </legend>
                            <div className="flex flex-wrap gap-3">
                                <Label
                                    htmlFor="deliveryType-pickup"
                                    className="flex items-center gap-2 text-sm font-medium leading-none"
                                >
                                    <input
                                        type="radio"
                                        id="deliveryType-pickup"
                                        name="deliveryType"
                                        value={DELIVERY_TYPE_PICKUP}
                                        checked={draft.deliveryType === DELIVERY_TYPE_PICKUP}
                                        onChange={(e) => setDraftField('deliveryType', e.target.value)}
                                        className="size-4"
                                    />
                                    PICK UP
                                </Label>
                                <Label
                                    htmlFor="deliveryType-delivered"
                                    className="flex items-center gap-2 text-sm font-medium leading-none"
                                >
                                    <input
                                        type="radio"
                                        id="deliveryType-delivered"
                                        name="deliveryType"
                                        value={DELIVERY_TYPE_DELIVERED}
                                        checked={draft.deliveryType === DELIVERY_TYPE_DELIVERED}
                                        onChange={(e) => setDraftField('deliveryType', e.target.value)}
                                        className="size-4"
                                    />
                                    VENDOR-DELIVERED
                                </Label>
                            </div>
                            {renderFieldError('deliveryType')}
                        </fieldset>
                    </div>

                    <div className="flex flex-col gap-2">
                        <VendorPicker
                            value={draft.vendorId}
                            onValueChange={(id) => setDraftField('vendorId', id)}
                            label={
                                <>
                                    Vendor <span className="text-destructive">*</span>
                                </>
                            }
                        />
                        {renderFieldError('vendorId')}
                    </div>

                    <div className="flex flex-col gap-2">
                        <BuyersPicker
                            value={draft.buyers}
                            onValueChange={(ids) => setDraftField('buyers', ids)}
                            label={
                                <>
                                    Buyer(s) <span className="text-destructive">*</span>
                                </>
                            }
                        />
                        {renderFieldError('buyers')}
                    </div>

                    <div className="flex flex-col gap-2">
                        <FacilityPickerV2
                            label={
                                <>
                                    Facility <span className="text-destructive">*</span>
                                </>
                            }
                            value={draft.facilities}
                            onValueChange={(facilities) => setDraftField('facilities', facilities)}
                            multiSelect
                        />
                        {renderFieldError('facilities')}
                    </div>

                    <div className="flex flex-col gap-2">
                        <ShippersPicker
                            value={draft.shipperId}
                            onValueChange={(id) => setDraftField('shipperId', id)}
                            label={
                                <>
                                    Shipper{' '}
                                    {draft.deliveryType === DELIVERY_TYPE_PICKUP ? (
                                        <span className="text-destructive">*</span>
                                    ) : null}
                                </>
                            }
                        />
                        {renderFieldError('shipperId')}
                    </div>

                    <div className="flex flex-col gap-2 sm:w-[260px]">
                        <span className={labelClassName}>Priority</span>
                        <div className="flex items-center gap-3">
                            <Checkbox
                                id="priority"
                                checked={draft.priority === true}
                                onCheckedChange={(checked) => setDraftField('priority', checked === true)}
                                aria-describedby="priority-helper"
                            />
                            <Label htmlFor="priority" className="text-sm font-medium leading-none">
                                Mark as Priority
                            </Label>
                        </div>
                    </div>

                    {/* Date + time + pickup group (spans 2 cols on xl) */}
                    <div className="flex flex-wrap items-end gap-3 sm:col-span-2 xl:col-span-2">
                        <div className="flex flex-col gap-2 sm:w-[260px]">
                            <Label htmlFor="originalRequestedDeliveryDate" className={labelClassName}>
                                Original Requested Delivery Date <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                id="originalRequestedDeliveryDate"
                                type="date"
                                value={draft.originalRequestedDeliveryDate}
                                onChange={handleInputChange('originalRequestedDeliveryDate')}
                            />
                            {renderFieldError('originalRequestedDeliveryDate')}
                        </div>

                        <div className="flex flex-col gap-2 sm:w-[260px]">
                            <Label htmlFor="scheduledDeliveryDate" className={labelClassName}>
                                Scheduled Delivery Date <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                id="scheduledDeliveryDate"
                                name="scheduledDeliveryDate"
                                type="date"
                                value={scheduledDeliveryDateInput}
                                onChange={handleScheduledDeliveryDateChange}
                                onBlur={handleScheduledDeliveryDateBlur}
                            />
                            {renderFieldError('scheduledDeliveryDate')}
                        </div>

                        <div className="flex flex-col gap-2 sm:w-[260px]">
                            <Label htmlFor="scheduledTime" className={labelClassName}>
                                Scheduled Delivery Time <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                id="scheduledTime"
                                name="scheduledTime"
                                type="time"
                                step="60"
                                value={draft.scheduledTime}
                                onChange={handleScheduledTimeChange}
                                disabled={!draft.scheduledDeliveryDate}
                            />
                            {renderFieldError('scheduledTime')}
                        </div>

                        <div className="flex flex-col gap-2 sm:w-[260px]">
                            <Label htmlFor="pickUpDate" className={labelClassName}>
                                Pick Up Date{' '}
                                {draft.deliveryType === DELIVERY_TYPE_PICKUP ? (
                                    <span className="text-destructive">*</span>
                                ) : null}
                            </Label>
                            <Input
                                id="pickUpDate"
                                type="date"
                                value={draft.pickUpDate}
                                onChange={handleInputChange('pickUpDate')}
                            />
                            {renderFieldError('pickUpDate')}
                        </div>
                    </div>

                    {/* Temperature lives in xl col 3 to align under Priority */}
                    <div className="flex flex-col gap-2 sm:w-[260px] xl:col-span-1">
                        <span className={labelClassName}>Temperature (Min - Max)</span>
                        <div className="flex items-center gap-2">
                            <Label
                                htmlFor="minTempReq"
                                className="text-sm font-medium text-foreground whitespace-nowrap"
                            >
                                Min
                            </Label>
                            <Input
                                id="minTempReq"
                                name="minTempReq"
                                type="number"
                                step="1"
                                className="w-24"
                                value={draft.minTempReq}
                                onChange={handleInputChange('minTempReq')}
                                placeholder="Min"
                            />
                            <Label
                                htmlFor="maxTempReq"
                                className="text-sm font-medium text-foreground whitespace-nowrap"
                            >
                                Max
                            </Label>
                            <Input
                                id="maxTempReq"
                                name="maxTempReq"
                                type="number"
                                step="1"
                                className="w-24"
                                value={draft.maxTempReq}
                                onChange={handleInputChange('maxTempReq')}
                                placeholder="Max"
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-2 sm:col-span-2 xl:col-span-2">
                        <Label htmlFor="specialInstructions" className={labelClassName}>
                            Special Instructions
                        </Label>
                        <Textarea
                            id="specialInstructions"
                            value={draft.specialInstructions}
                            onChange={handleInputChange('specialInstructions')}
                            placeholder="Enter any special instructions for this purchase order…"
                            rows={4}
                        />
                    </div>

                    {draft.deliveryType === DELIVERY_TYPE_DELIVERED ? (
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="estimatedFreightCost" className={labelClassName}>
                                Freight Cost
                            </Label>
                            <Input
                                id="estimatedFreightCost"
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                className="w-24"
                                value={draft.estimatedFreightCost}
                                onChange={handleInputChange('estimatedFreightCost')}
                            />
                        </div>
                    ) : null}

                    {/* Cross Dock fields grouped together */}
                    <div className="flex flex-col gap-2 sm:col-span-2 xl:col-span-3">
                        <Label className={labelClassName}>Cross Dock</Label>
                        <div className="w-full max-w-md">
                            <div className="flex items-center justify-between gap-3 rounded-md border border-input bg-card px-3 py-2">
                                <span className="text-sm text-foreground">Include Cross Dock fields</span>
                                <Switch
                                    checked={crossDockEnabled}
                                    onCheckedChange={handleCrossDockSwitchChange}
                                    aria-label="Toggle cross dock fields"
                                />
                            </div>
                        </div>

                        {crossDockEnabled ? (
                            <div className="flex flex-wrap items-end gap-3 pt-2">
                                <div className="flex flex-col gap-2 sm:w-[260px]">
                                    <CrossDockPicker
                                        value={draft.crossDockId}
                                        onValueChange={(id) => setDraftField('crossDockId', id)}
                                    />
                                    {renderFieldError('crossDockId')}
                                </div>

                                <div className="flex flex-col gap-2 sm:w-[260px]">
                                    <Label htmlFor="crossDockDeliveryDate" className={labelClassName}>
                                        Cross Dock Delivery Date
                                    </Label>
                                    <Input
                                        id="crossDockDeliveryDate"
                                        type="date"
                                        value={draft.crossDockDeliveryDate}
                                        onChange={handleInputChange('crossDockDeliveryDate')}
                                    />
                                </div>

                                <div className="flex flex-col gap-2 sm:w-[260px]">
                                    <span className={labelClassName}>&nbsp;</span>
                                    <div className="flex items-center gap-3">
                                        <Checkbox
                                            id="isReceivedAtCrossDock"
                                            checked={draft.isReceivedAtCrossDock === true}
                                            onCheckedChange={(checked) =>
                                                setDraftField('isReceivedAtCrossDock', checked === true)
                                            }
                                        />
                                        <Label
                                            htmlFor="isReceivedAtCrossDock"
                                            className="text-sm font-medium leading-none"
                                        >
                                            Received at Cross Dock
                                        </Label>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <Dialog open={crossDockConfirmOpen} onOpenChange={setCrossDockConfirmOpen}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Clear cross dock data?</DialogTitle>
                                <DialogDescription>
                                    Cross dock values are currently filled in. Turning this off can clear those values.
                                    Would you like to clear cross dock data or keep it?
                                </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setCrossDockConfirmOpen(false)}>
                                    Keep data
                                </Button>
                                <Button
                                    type="button"
                                    variant="destructive"
                                    onClick={() => {
                                        clearCrossDockDraftFields();
                                        setShowCrossDockFields(false);
                                        setCrossDockConfirmOpen(false);
                                    }}
                                >
                                    Clear data
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </CardContent>
        </Card>
    );
}

PurchaseOrderCreatePrimaryCard.displayName = 'PurchaseOrderCreatePrimaryCard';
